import { useState, useEffect, useRef } from "react";
import ReactGA from "react-ga4";
import useMediaQuery from "@mui/material/useMediaQuery";
import { IoIosMenu } from "react-icons/io";

export const Navigation = () => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [isOpen, setIsOpen] = useState(!isMobile);
  const [activeMenu, setActiveMenu] = useState("Home");
  const sectionRefs = useRef({});

  const menuItems = [
    { href: "#home", label: "Home" },
    { href: "#about", label: "About" },
    { href: "#platform", label: "Platform" },
    // { href: "#howItWorks", label: "How It Works" },
    { href: "#contact", label: "Contact" },
  ];

  useEffect(() => {
    setIsOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    menuItems.forEach(({ href, label }) => {
      sectionRefs.current[label] = document.querySelector(href);
    });
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY + window.innerHeight / 3;
      let current = activeMenu;
      for (let { label } of menuItems) {
        const sec = sectionRefs.current[label];
        if (sec) {
          const top = sec.offsetTop;
          const h = sec.offsetHeight;
          if (scrollY >= top && scrollY < top + h) {
            current = label;
            break;
          }
        }
      }
      setActiveMenu(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [activeMenu]);

  const handleToggle = () => setIsOpen(!isOpen);

  const handleLinkClick = (e, label) => {
    e.preventDefault();
    ReactGA.event({ category: "User", action: `Clicked on ${label}` });
    setActiveMenu(label);
    setIsOpen(false);
    document.querySelector(`#${label.toLowerCase().replace(/\s+/g, "")}`)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav className="fixed top-0 left-0 w-full bg-white shadow-md z-50 py-6">
      <div className="max-w-[1200px] mx-auto px-6 flex items-center">
        <a href="#home" onClick={(e) => handleLinkClick(e, "Home")} className="flex-shrink-0 pl-4">
          <img
            src="https://circuland-website-images.s3.eu-west-2.amazonaws.com/Logo+(colour).png"
            alt="Circuland Logo"
            className="h-16 w-auto"
          />
        </a>
        <div className="flex items-center ml-auto space-x-8">
          {!isMobile && (
            <ul className="flex space-x-8 text-[16px] font-lato text-gray-600">
              {menuItems.map(({ href, label }) => (
                <li key={href} onClick={(e) => handleLinkClick(e, label)}>
                  <a
                    href={href}
                    className={`
                      relative px-2 py-2.5
                      after:content-['']
                      after:absolute after:-bottom-1 after:left-0
                      after:h-1 after:bg-indigo-600
                      after:transition-all after:duration-700 after:ease-in-out
                      ${activeMenu === label ? "after:w-full" : "after:w-0"}
                    `}
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          )}
          {!isMobile && (
            <a
              href="https://outlook.office365.com/owa/calendar/MeetingwithCirculand@circuland.co.uk/bookings/"
              onClick={() =>
                ReactGA.event({
                  category: "User",
                  action: "Clicked on Request Demo",
                })
              }
              className="px-6 py-2 text-white font-medium rounded-[38px] shadow-md transition-transform transform hover:scale-105 bg-[#AAAAAA] hover:text-white"
            >
              Request Demo
            </a>
          )}
          {isMobile && (
            <button
              onClick={handleToggle}
              className="ml-4 p-2 rounded-md text-black hover:bg-gray-100 focus:outline-none focus:ring"
            >
              {/* <div className="space-y-1">
                <span className="block w-6 h-0.5 bg-current" />
                <span className="block w-6 h-0.5 bg-current" />
                <span className="block w-6 h-0.5 bg-current" />
              </div> */}
              <IoIosMenu size={30} className="text-gray-700" />
            </button>
          )}
        </div>
      </div>
      {isMobile && isOpen && (
        <div className="overflow-x-auto whitespace-nowrap bg-white py-4 px-6">
          {menuItems.map(({ href, label }) => (
            <a
              key={href}
              href={href}
              onClick={(e) => handleLinkClick(e, label)}
              className={`
                inline-block mx-4 py-3 text-[16px] font-lato
                transition-all duration-700 ease-in-out
                ${activeMenu === label ? "border-b-2 border-indigo-600" : ""}
              `}
            >
              {label}
            </a>
          ))}
          <a
            href="https://outlook.office365.com/owa/calendar/MeetingwithCirculand@circuland.co.uk/bookings/"
            onClick={() =>
              ReactGA.event({
                category: "User",
                action: "Clicked on Request Demo",
              })
            }
            className="inline-block mx-4 px-5 py-3 text-white font-medium rounded-lg shadow-md bg-[#AAAAAA] hover:text-white transition-transform transform hover:scale-105"
          >
            Request Demo
          </a>
        </div>
      )}
    </nav>
  );
};
